import * as React from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { fetchRequestLinkedAdvance, fetchTravelRequest } from "../../api";
import { Advance, AdvanceAmount, ClaimMethod, ExpenseLineItem, FxOverride, TravelRequestSummary } from "../../types";
import { billTotals } from "@/lib/bills";
import {
  computeSettlementAmount,
  formatSettlementAmount,
  SettlementAmount
} from "@/lib/settlementAmount";

const EMPTY_ADVANCE_AMOUNTS: AdvanceAmount[] = [];

/** Renders the resolved SettlementAmount list the same way everywhere -
 *  "-" for nothing to show (no bills/advance at all), one stacked line per
 *  entry otherwise (same multi-line pattern AmountValue below already uses
 *  for several currencies), each coloured by its own direction, same colour
 *  convention OffsetCell/the ledger's own tiles use (primary = payable to
 *  the employee, destructive = owed by them). Either claim method can be
 *  several entries when its rows target different settlement currencies
 *  (see lib/settlementAmount.ts's own doc comments on
 *  fixedAllowanceSettlement/reimbursementSettlement). A zero-amount entry is dropped
 *  (nothing to show for that currency), same as the old single-value
 *  behaviour - "-" only when EVERY entry is zero or the list itself is
 *  empty/null. */
function SettlementAmountValue({ value }: { value: SettlementAmount[] | null }) {
  const lines = (value ?? []).filter((v) => v.amount !== 0);
  if (lines.length === 0) {
    return <span className="text-sm text-muted-foreground">-</span>;
  }
  return (
    <div className="whitespace-nowrap text-sm">
      {lines.map((line) => (
        <div
          key={line.currency}
          className={line.direction === "payable" ? "font-medium text-primary" : "font-medium text-destructive"}
        >
          {formatSettlementAmount(line)}
        </div>
      ))}
    </div>
  );
}

/** Renders the same multi-line "request currency first, then every other
 *  currency" layout MyRequests.tsx/OtherReimbursements.tsx's own Amount
 *  column cell already uses, factored out here so AmountCellById matches it
 *  exactly instead of drifting into a second, slightly different copy. */
function AmountValue({ totals, currency }: { totals: ReturnType<typeof billTotals>; currency: string }) {
  const lines = [
    ...(totals.totalInRequestCurrency > 0
      ? [`${currency} ${totals.totalInRequestCurrency.toLocaleString()}`]
      : []),
    ...totals.others.map((o) => `${o.currency} ${o.total.toLocaleString()}`)
  ];
  return lines.length > 0 ? (
    <div className="whitespace-nowrap text-sm">
      {lines.map((line) => (
        <div key={line}>{line}</div>
      ))}
    </div>
  ) : (
    <span className="text-sm">-</span>
  );
}

/** The "Settlement amount" column's cell for a row that already carries its
 *  own full data (My Reimbursements/Other Reimbursements, whose list fetch
 *  already returns the whole TravelRequestSummary - see requests_store.py's
 *  list_for_user) - still needs its own linked advance fetched (the list
 *  endpoint doesn't resolve it) and, for a multi-currency Reimbursement,
 *  live FX rates (see lib/settlementAmount.ts), so this briefly shows a
 *  skeleton, same loading pattern as the Request Details dialog's own
 *  ledger - just once per visible row instead of once per opened dialog. */
export function SettlementAmountCell({
  requestId,
  claimMethod,
  currency,
  expenses,
  estimatedAllowanceAmount,
  departureDate,
  linkedAdvanceId,
  settlementTargetByCurrency,
  fxOverrides
}: {
  requestId: string;
  claimMethod: ClaimMethod;
  currency: string;
  expenses: ExpenseLineItem[];
  estimatedAllowanceAmount: number;
  departureDate: string;
  linkedAdvanceId?: string | null;
  /** Saved per-currency settlement-currency choices from
   *  CurrencyConversionCard's own pickers (TravelRequestSummary.
   *  settlementTargetByCurrency) - without this, the column always nets
   *  into `currency` even after every row was explicitly repointed
   *  elsewhere in the Request Details dialog (see lib/settlementAmount.ts's
   *  own doc comment for the live bug this closes). */
  settlementTargetByCurrency?: Record<string, string>;
  /** Saved FX rate overrides (TravelRequestSummary.fxOverrides) - without
   *  this, a currency pair with a custom override rate was recomputed from
   *  a fresh live rate instead, which could disagree with the Request
   *  Details dialog's own figure for the same request (see
   *  lib/settlementAmount.ts's own doc comment for the live bug this
   *  closes). */
  fxOverrides?: FxOverride[];
}) {
  const [value, setValue] = React.useState<SettlementAmount[] | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      let advanceAmounts: AdvanceAmount[] = EMPTY_ADVANCE_AMOUNTS;
      if (linkedAdvanceId) {
        // Request-scoped, not fetchAdvance's own (requester/approver)
        // scoping - this column is shown to the requester AND to an
        // approver of the REQUEST, who may not be the advance's own
        // approver (same reasoning as RequestDetailDialog's identical
        // fetch).
        const advance = await fetchRequestLinkedAdvance(requestId).catch(() => null);
        if (advance) advanceAmounts = advance.amounts;
      }
      if (cancelled) return;
      const result = await computeSettlementAmount(
        {
          claimMethod,
          currency,
          expenses,
          estimatedAllowanceAmount,
          departureDate,
          settlementTargetByCurrency,
          fxOverrides
        },
        advanceAmounts
      ).catch(() => null);
      if (!cancelled) {
        setValue(result);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    requestId,
    claimMethod,
    currency,
    expenses,
    estimatedAllowanceAmount,
    departureDate,
    linkedAdvanceId,
    settlementTargetByCurrency,
    fxOverrides
  ]);

  if (loading) return <Skeleton className="h-4 w-20" />;
  return <SettlementAmountValue value={value} />;
}

/** My Approvals/Approver Queue's row (Approval/AdvanceApproval) carries only
 *  trip fields + status, not expenses/currency/claimMethod/linkedAdvanceId
 *  (see types.ts's own doc comments) - both the Amount and Settlement
 *  amount columns there need the full request document (fetchTravelRequest)
 *  plus its linked advance (fetchRequestLinkedAdvance). Shared by a tiny
 *  per-request-id cache so a row showing BOTH columns fetches each exactly
 *  once, not twice - every row on a page is a distinct request id, so this
 *  never grows unbounded within one page view, and a fresh page load starts
 *  it empty again (no cross-request-lifetime staleness to worry about). */
const detailCache = new Map<
  string,
  Promise<{ detail: TravelRequestSummary | null; advance: Advance | null }>
>();
function loadDetailAndAdvance(requestId: string) {
  let entry = detailCache.get(requestId);
  if (!entry) {
    entry = (async () => {
      const detail = await fetchTravelRequest(requestId).catch(() => null);
      if (!detail) return { detail: null, advance: null };
      const advance = detail.linkedAdvanceId
        ? await fetchRequestLinkedAdvance(requestId).catch(() => null)
        : null;
      return { detail, advance };
    })();
    detailCache.set(requestId, entry);
  }
  return entry;
}

/** The "Amount" column's cell for an approval-queue row - same bill-totals
 *  presentation as MyRequests.tsx's own Amount column, just sourced from
 *  the fetched-by-id detail instead of data already on the row. */
export function AmountCellById({ requestId }: { requestId: string }) {
  const [detail, setDetail] = React.useState<TravelRequestSummary | null | undefined>(undefined);

  React.useEffect(() => {
    let cancelled = false;
    loadDetailAndAdvance(requestId).then(({ detail }) => {
      if (!cancelled) setDetail(detail);
    });
    return () => {
      cancelled = true;
    };
  }, [requestId]);

  if (detail === undefined) return <Skeleton className="h-4 w-20" />;
  if (!detail) return <span className="text-sm">-</span>;
  const totals = billTotals(
    detail.expenses,
    detail.currency,
    detail.estimatedAllowanceAmount,
    detail.policyLimitsByCurrencyAtSubmission
  );
  return <AmountValue totals={totals} currency={detail.currency} />;
}

/** The "Settlement amount" column's cell for an approval-queue row - see
 *  AmountCellById above for why this needs the fetched-by-id detail rather
 *  than data already on the row, and loadDetailAndAdvance's own doc comment
 *  for why the two cells share one fetch instead of duplicating it. */
export function SettlementAmountCellById({ requestId }: { requestId: string }) {
  const [value, setValue] = React.useState<SettlementAmount[] | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadDetailAndAdvance(requestId)
      .then(async ({ detail, advance }) => {
        if (!detail) return null;
        return computeSettlementAmount(
          {
            claimMethod: detail.claimMethod,
            currency: detail.currency,
            expenses: detail.expenses,
            estimatedAllowanceAmount: detail.estimatedAllowanceAmount,
            departureDate: detail.departureAt || detail.createdAt,
            settlementTargetByCurrency: detail.settlementTargetByCurrency,
            fxOverrides: detail.fxOverrides
          },
          advance?.amounts ?? EMPTY_ADVANCE_AMOUNTS
        );
      })
      .catch(() => null)
      .then((result) => {
        if (!cancelled) {
          setValue(result ?? null);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [requestId]);

  if (loading) return <Skeleton className="h-4 w-20" />;
  return <SettlementAmountValue value={value} />;
}
